import { transformCodeToDate } from "./utils";
import { useDataStore, compareNumeroPbr } from '../stores/useDataStore';
import { computed, reactive, ref } from "vue";
import { createOrUpdateManualValue, updateCommentaire, fetchCommentaire } from '../functions/functions_pilotage_api';

export const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
export const localCommentaire = ref(''); // Initialize empty

export function initializeLocalCommentaire() {
  const dataStore = getDataStore();
  localCommentaire.value = dataStore.commentaire?.commentaire ?? '';
}

function getDataStore() {
  return useDataStore();
}

export function computeAbsDelta(prev: Prelevement, current: Prelevement, volume: number | null): number | null {
  if (!prev || !current) return null;

  const { absorbance_880nm: a1, daycode: d1, vol_recolte } = prev;
  const { absorbance_880nm: a2, daycode: d2 } = current;

  if (a1 === undefined || a2 === undefined || !d1 || !d2) return null;

  const date1 = transformCodeToDate(d1);
  const date2 = transformCodeToDate(d2);
  if (!date1 || !date2) return null;

  const diffMs = new Date(date2).getTime() - new Date(date1).getTime();
  const diffDays = Math.abs(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return null;

  let absorbance1: number | null = a1;
  if (vol_recolte && vol_recolte !== 0 && a1 !== null) {
    if (volume === 0 || volume === null) return null;
    absorbance1 = a1 * (volume - vol_recolte) / volume;
  }

  if (a2 === null || absorbance1 === null) return null
  return +((a2 - absorbance1) / diffDays);
}

export const getDeltaAbsorbanceGrowth = (culture: Culture): number | null => {
  const prelevements = (culture.prelevements || [])
    .filter(p => p.absorbance_880nm !== undefined && p.daycode)
    .sort((a, b) => a.daycode.localeCompare(b.daycode));

  if (prelevements.length < 2) return null;

  const last = prelevements[prelevements.length - 1];
  const previous = prelevements[prelevements.length - 2];

  const delta = computeAbsDelta(previous, last, culture.pbr.volume);
  return delta !== null ? +delta.toFixed(2) : null;
};

export function getThresholdsClass(threshold: string, specieId: number, value: number | null): string {
  const dataStore = useDataStore();

  // Vérifier si la valeur est valide
  if (value === 0 || value === null || isNaN(value)) {
    return '';
  }

  // Extraire les seuils selon le type
  let thresholds: (number | null)[] = []; // Initialisation par défaut à un tableau vide
  const specieThresholds = dataStore.thresholds[specieId];

  if (!specieThresholds || !Array.isArray(specieThresholds)) {
    // Si pas de seuils pour cette espèce, retourne un tableau de nulls selon le type attendu
    if (threshold === 'ic') {
      thresholds = [null, null, null]; // 3 valeurs pour Ic
    } else if (threshold === 'ProdMs' || threshold === 'ProdMi') {
      thresholds = [null, null]; // 2 valeurs pour ProdMs ou ProdMi
    }
  } else {
    // Seuils disponibles, extraction normale
    if (threshold === 'ic') {
      thresholds = specieThresholds.slice(0, 3);
    } else if (threshold === 'ProdMs') {
      thresholds = specieThresholds.slice(3, 5);
    } else if (threshold === 'ProdMi') {
      thresholds = specieThresholds.slice(5, 7);
    }
  }

  if (thresholds.length === 0) {
    return ''; // Ou gère selon le besoin (ex. : return null)
  }

  // Vérifier si tous les seuils nécessaires sont non null
  if (thresholds.some((t) => t === null)) {
    return '';
  }

  // Trier les seuils (sûrs d'être des nombres après la vérification)
  const sortedThresholds = thresholds.sort((a, b) => (a as number) - (b as number)) as number[];

  // Appliquer la logique des couleurs
  if (threshold === 'ic') {
    if (value <= sortedThresholds[0]) return 'growth-red';
    if (value <= sortedThresholds[1]) return 'growth-orange';
    if (value <= sortedThresholds[2]) return 'growth-light-green';
    return 'growth-dark-green';
  } else if (threshold === 'ProdMs' || threshold === 'ProdMi') {
    if (value <= sortedThresholds[0]) return 'prod-pink';
    if (value <= sortedThresholds[1]) return 'prod-orange';
    return 'prod-green';
  }

  return '';
}

const latestManualUpdate = computed(() => {
  const dataStore = getDataStore();
  const updates = Object.values(dataStore.manualValues)
    .map(val => val.last_updated)
    .filter(Boolean)
    .map(date => new Date(date!));

  return updates.length ? updates.sort((a, b) => b.getTime() - a.getTime())[0] : null;
});

export const latestManualUpdateFormatted = computed(() => {
  if (!latestManualUpdate.value) return '';

  return latestManualUpdate.value.toLocaleString('fr-FR', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
});

export const computeAge = (culture: Culture): number => {
  const startDateStr = transformCodeToDate(culture.startdaycode.startday_code);
  const startDate = new Date(startDateStr);
  const today = new Date();
  const diff = today.getTime() - startDate.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

export const prochainesRecoltesParEspeceEtJour = computed(() => {
  const dataStore = getDataStore();
  const result: Record<string, Record<string, string[]>> = {}

  for (const [id, entry] of Object.entries(dataStore.manualValues)) {
    const jour = entry?.prochaine_recolte;
    const culture = dataStore.cultures.filter((c: Culture) => c.active).find(c => c.id === Number(id))
    const espece = culture?.specie?.specie_code
    const pbr = culture?.pbr?.numero_pbr

    if (!jour || !espece || !pbr) continue

    if (!result[espece]) {
      result[espece] = {}
      for (const day of days) {
        result[espece][day] = []
      }
    }
    
    if (!result[espece][jour]) {
      result[espece][jour] = []
    }

    result[espece][jour].push(pbr)
  }

  return result
})

export function onCommentInput(e: Event) {
  const target = e.target as HTMLTextAreaElement;
  localCommentaire.value = target.value;
}

export async function saveCommentaire() {
  const dataStore = getDataStore();

  // Crée l'objet commentaire si inexistant
  if (!dataStore.commentaire) {
    dataStore.commentaire = { id: 0, commentaire: localCommentaire.value };
  } else {
    dataStore.commentaire.commentaire = localCommentaire.value;
  }

  try {
    await updateCommentaire(dataStore.commentaire);
    await fetchCommentaire();
  } catch (error) {
    console.error('Erreur lors de la sauvegarde du commentaire', error);
  }
}


export function onManualValueInput(value: string, cultureId: number, field: EditableManualValueField) {
  const dataStore = getDataStore();
  let manual = dataStore.manualValues[cultureId];

  if (!manual) {
    // Initialise un objet complet et réactif
    manual = reactive({
      id: 0,
      culture_id: cultureId,
      pompe: null,
      physiologie: null,
      actions_prevue: null,
      prochaine_recolte: null,
      last_updated: null
    });
    dataStore.manualValues[cultureId] = manual;
  }

  if (manual[field] === value) {
    return;
  }
  manual[field] = value;

  createOrUpdateManualValue(manual)
    .then((updatedManual) => {
      dataStore.manualValues[cultureId] = reactive(updatedManual);
      console.info(`${field} ${cultureId} updated`);
    })
    .catch(console.error);
}

export async function onInput(e: Event, cultureId: number, field: EditableManualValueField) {
  const target = e.target as HTMLInputElement | HTMLSelectElement | null;
  if (!target) return;

  const dataStore = useDataStore();
  const value = target.value;

  // On met à jour localement
  if (!dataStore.manualValues[cultureId]) {
    dataStore.manualValues[cultureId] = {
      id: 0,
      culture_id: cultureId,
      physiologie: null,
      actions_prevue: null,
      prochaine_recolte: null,
      last_updated: null
    };
  }
  dataStore.manualValues[cultureId][field] = value;

  // On récupère la culture pour ajouter les infos nécessaires
  const culture = dataStore.cultures.find(c => c.id === cultureId);
  if (!culture) return;

  const manualValueObj = {
    ...dataStore.manualValues[cultureId],
    specie_id: culture.specie.id,
    culture_id: cultureId
  };

  // On envoie direct à l'API
  await createOrUpdateManualValue(manualValueObj);
}

// Organiser les cultures actives par zone
export const culturesGroupedByZone = computed(() => {
  const dataStore = getDataStore();
  const validZones = ['zoneA', 'zoneB', 'zoneC'] as const;
  type Zone = typeof validZones[number];

  const groups: Record<Zone, Culture[]> = {
    zoneA: [],
    zoneB: [],
    zoneC: [],
  };

  for (const culture of dataStore.cultures) {
    if (!culture.active) continue;

    groups[culture.pbr.zone as keyof typeof groups].push(culture);
  }

  for (const zone of validZones) {
    groups[zone].sort((a, b) =>
      compareNumeroPbr(a.pbr.numero_pbr, b.pbr.numero_pbr)
    );
  }

  return groups;
});